// ============================================================
//  src/pages/wallet/WalletPage.js
//
//  Prepaid CommandKit wallet: top up with PocketFi, pay plans and
//  Rent Space rent from the balance, switch auto-renew on per plan /
//  rental, and see every naira in and out. Styles: WalletPage.css (wal-).
//
//  Top-up return: PocketFi sends the user back to /wallet?topup=1. The
//  checkout id was saved before leaving; we ask the server to confirm it
//  with PocketFi (it credits once, however many times this runs).
// ============================================================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getWallet, walletTopup, walletVerifyTopup, walletPayPlan, walletPayRent, walletAutoRenew } from '../../api/client';
import './WalletPage.css';

const PENDING_KEY = 'ck_pending_topup_id';
const CHIPS = [500, 1000, 2000, 5000, 10000, 20000];

const naira = (n, dp = 0) => '₦' + (Number(n) || 0).toLocaleString('en-NG', { minimumFractionDigits: dp, maximumFractionDigits: 2 });
const errMsg = (e, f = 'Something went wrong') => e?.response?.data?.message ?? f;
const nonce = () => (window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/[^A-Za-z0-9_-]/g, '');
function parseDate(v) { if (!v) return null; const d = new Date(String(v).replace(' ', 'T')); return Number.isNaN(d.getTime()) ? null : d; }
function fmtDate(v, time = false) {
  const d = parseDate(v);
  if (!d) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', ...(time ? { hour: 'numeric', minute: '2-digit' } : {}) });
}
function dueIn(v) {
  const d = parseDate(v);
  if (!d) return '';
  const days = Math.ceil((d.getTime() - Date.now()) / 86400000);
  if (days < 0) return `ended ${Math.abs(days)} day${days === -1 ? '' : 's'} ago`;
  if (days === 0) return 'ends today';
  return `${days} day${days === 1 ? '' : 's'} left`;
}
const KIND = {
  topup: { icon: 'fa-arrow-down', label: 'Top-up' },
  plan:  { icon: 'fa-crown',      label: 'Plan' },
  rent:  { icon: 'fa-sim-card',   label: 'Rent Space' },
  branding: { icon: 'fa-wand-magic-sparkles', label: 'Remove branding' },
  contact_saver: { icon: 'fa-address-book', label: 'Contact Saver' },
  admin: { icon: 'fa-user-shield', label: 'Adjustment by CommandKit' },
};

function Switch({ on, onChange, disabled, label }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} className={`wal-switch ${on ? 'is-on' : ''}`} onClick={() => onChange(!on)} disabled={disabled}>
      <span />
    </button>
  );
}

export default function WalletPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [amount, setAmount] = useState('2000');
  const [starting, setStarting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState('');                 // 'plan' | `rent:<id>` | `auto:...`
  const [confirm, setConfirm] = useState(null);         // { kind:'plan'|'rent', ... }
  const [ledger, setLedger] = useState([]);
  const [moreBusy, setMoreBusy] = useState(false);
  const checkedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const res = await getWallet(0);
      if (res.data?.success) { setData(res.data); setLedger(res.data.ledger || []); setError(''); }
      else setError(res.data?.message ?? 'Could not load your wallet');
    } catch (e) { setError(errMsg(e, 'Could not load your wallet')); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Back from PocketFi → confirm the top-up (a few tries: PocketFi can say "pending" for a moment)
  useEffect(() => {
    if (checkedRef.current) return;
    const params = new URLSearchParams(location.search);
    let id = null;
    try { id = localStorage.getItem(PENDING_KEY); } catch { /* storage blocked */ }
    if (!params.has('topup') || !id) return;
    checkedRef.current = true;
    navigate('/wallet', { replace: true });
    (async () => {
      setChecking(true);
      for (let i = 0; i < 4; i++) {
        try {
          const res = await walletVerifyTopup(id);
          const st = res.data?.status;
          if (st === 'success') {
            try { localStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
            toast.success(res.data?.credited ? `${naira(res.data.credited)} added to your wallet` : 'Wallet topped up');
            break;
          }
          if (st === 'failed' || st === 'cancelled') {
            try { localStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
            toast.error(st === 'cancelled' ? 'Top-up was cancelled — nothing was charged' : 'Top-up failed — nothing was charged');
            break;
          }
          if (i === 3) toast('Payment is still processing — your balance updates as soon as PocketFi confirms it.', { icon: '⏳', duration: 7000 });
        } catch (e) { if (i === 3) toast.error(errMsg(e, 'Could not check the payment')); }
        await new Promise(r => setTimeout(r, 2500));
      }
      setChecking(false);
      load();
    })();
  }, [location.search, navigate, load]);

  const min = data?.minTopup ?? 500;
  const max = data?.maxTopup ?? 1000000;
  const amt = Math.floor(Number(String(amount).replace(/[^\d]/g, '')) || 0);
  const amtOk = amt >= min && amt <= max;

  async function startTopup() {
    if (!amtOk) { toast.error(`Top up between ${naira(min)} and ${naira(max)}`); return; }
    setStarting(true);
    try {
      const res = await walletTopup(amt, `${window.location.origin}/wallet`);
      if (res.data?.success && res.data.payment_link) {
        try { localStorage.setItem(PENDING_KEY, res.data.payment_id); } catch { /* ignore */ }
        window.location.href = res.data.payment_link;
        return;
      }
      toast.error(res.data?.message ?? 'Could not start payment');
    } catch (e) { toast.error(errMsg(e, 'Could not start payment')); }
    setStarting(false);
  }

  async function toggleAuto(target, on, rnId) {
    const key = `auto:${target}:${rnId ?? ''}`;
    setBusy(key);
    // optimistic
    setData(d => target === 'plan' ? { ...d, autoRenewPlan: on } : { ...d, rentals: d.rentals.map(r => r.rnId === rnId ? { ...r, autoRenew: on } : r) });
    try {
      const res = await walletAutoRenew(target, on, rnId);
      toast.success(res.data?.message ?? (on ? 'Auto-renew on' : 'Auto-renew off'));
    } catch (e) { toast.error(errMsg(e)); load(); }
    setBusy('');
  }

  async function doPay() {
    const c = confirm;
    setBusy(c.kind === 'plan' ? 'plan' : `rent:${c.rnId}`);
    try {
      const res = c.kind === 'plan' ? await walletPayPlan(c.slug, c.nonce) : await walletPayRent(c.rnId, c.nonce);
      toast.success(res.data?.message ?? 'Paid');
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
      if (e?.response?.data?.code === 'insufficient') setConfirm(null);
    }
    setBusy('');
  }

  async function loadMore() {
    setMoreBusy(true);
    try {
      const res = await getWallet(ledger.length);
      setLedger(l => [...l, ...(res.data?.ledger || [])]);
    } catch (e) { toast.error(errMsg(e)); }
    setMoreBusy(false);
  }

  if (error && !data) {
    return <div className="wal"><div className="wal-alert"><i className="fa-solid fa-circle-exclamation" /> {error} <button type="button" className="wal-link" onClick={load}>Try again</button></div></div>;
  }
  if (!data) {
    return <div className="wal"><div className="wal-skel"><span className="h" /><span /><span /></div></div>;
  }

  const plan = data.plan || {};
  const canPayPlan = !!plan.slug && plan.price > 0;
  const balance = Number(data.balance) || 0;
  const needs = [
    ...(canPayPlan && data.autoRenewPlan ? [plan.price] : []),
    ...data.rentals.filter(r => r.autoRenew && r.registered).map(r => r.price),
  ];
  const nextNeed = needs.length ? Math.max(...needs) : 0;
  const short = nextNeed > 0 && balance < nextNeed;

  return (
    <div className="wal">
      <header className="wal-head">
        <div>
          <h1>Wallet</h1>
          <p>Top up once, then pay your plan and Rent Space from your balance — or let it renew itself.</p>
        </div>
        <Link to="/billing" className="wal-btn ghost"><i className="fa-solid fa-credit-card" /> Billing</Link>
      </header>

      {checking && <div className="wal-alert is-info"><span className="wal-spin dark" /> Confirming your top-up with PocketFi…</div>}

      <div className="wal-top">
        {/* Balance + top up */}
        <section className="wal-balance">
          <div className="wal-balance-glow" aria-hidden="true" />
          <span className="wal-eyebrow"><i className="fa-solid fa-wallet" /> Available balance</span>
          <div className="wal-amount">{naira(balance, 2)}</div>
          {short && (
            <div className="wal-short"><i className="fa-solid fa-triangle-exclamation" /> {naira(nextNeed - balance)} more needed for your next auto-renewal</div>
          )}

          <div className="wal-topup">
            <label htmlFor="wal-amt">Top up</label>
            <div className="wal-chips">
              {CHIPS.map(c => (
                <button key={c} type="button" className={amt === c ? 'is-on' : ''} onClick={() => setAmount(String(c))}>{naira(c)}</button>
              ))}
            </div>
            <div className="wal-topup-row">
              <div className={`wal-input ${amount && !amtOk ? 'is-bad' : ''}`}>
                <span>₦</span>
                <input id="wal-amt" inputMode="numeric" value={amount ? Number(amt).toLocaleString('en-NG') : ''} placeholder="Amount"
                  onChange={e => setAmount(e.target.value.replace(/[^\d]/g, '').slice(0, 7))}
                  onKeyDown={e => { if (e.key === 'Enter') startTopup(); }} />
              </div>
              <button type="button" className="wal-btn brand" onClick={startTopup} disabled={starting || !amtOk || !data.pocketfiReady}>
                {starting ? <><span className="wal-spin" /> Opening…</> : <>Top up <i className="fa-solid fa-arrow-right" /></>}
              </button>
            </div>
            <span className="wal-fine">
              {data.pocketfiReady ? <>Min {naira(min)} · secure checkout via PocketFi</> : 'Online payments are unavailable right now.'}
            </span>
          </div>
        </section>

        {/* How it works */}
        <section className="wal-how">
          <h2>How your wallet works</h2>
          <ul>
            <li><i className="fa-solid fa-bolt" /><span><b>Pay instantly.</b> Plans and rent are paid from your balance in one tap — no checkout.</span></li>
            <li><i className="fa-solid fa-rotate" /><span><b>Auto-renew is optional.</b> Turn it on below and we renew {data.renewAheadHours || 24} hours before the end date, so your bots never pause.</span></li>
            <li><i className="fa-solid fa-envelope" /><span><b>Low balance?</b> We email you once before the date so you can top up.</span></li>
            <li><i className="fa-solid fa-lock" /><span><b>Prepaid credit.</b> Your balance can be spent on CommandKit plans and rent; it can’t be withdrawn.</span></li>
          </ul>
        </section>
      </div>

      {/* Auto-renew & pay */}
      <section className="wal-card">
        <header className="wal-card-head">
          <div><h2>Renewals</h2><p>Pay now from your balance, or switch on auto-renew.</p></div>
        </header>

        <div className="wal-rows">
          <div className="wal-row">
            <span className="wal-row-ic is-plan"><i className="fa-solid fa-crown" /></span>
            <div className="wal-row-main">
              <b>{plan.name || 'Plan'}</b>
              <span>
                {canPayPlan ? `${naira(plan.price)} / ${plan.interval || 30} days · ` : ''}
                {plan.expiresAt ? `${plan.state === 'expired' ? 'Ended' : 'Ends'} ${fmtDate(plan.expiresAt)} (${dueIn(plan.expiresAt)})` : 'No end date'}
              </span>
              {!canPayPlan && <span className="wal-row-note">{plan.isTrial ? 'You’re on the free trial — choose a plan on Billing first, then it can renew from here.' : ''}</span>}
            </div>
            <div className="wal-row-actions">
              {canPayPlan ? (
                <>
                  <label className="wal-auto">
                    <span>Auto-renew</span>
                    <Switch on={!!data.autoRenewPlan} disabled={busy === 'auto:plan:'} onChange={v => toggleAuto('plan', v)} label="Auto-renew plan" />
                  </label>
                  <button type="button" className="wal-btn dark sm" disabled={!!busy}
                    onClick={() => setConfirm({ kind: 'plan', slug: plan.slug, title: `${plan.state === 'expired' ? 'Renew' : 'Pay for'} ${plan.name}`, price: plan.price, nonce: nonce(),
                      detail: plan.state === 'expired' || !plan.expiresAt ? `Starts now and runs ${plan.interval || 30} days.` : `Adds ${plan.interval || 30} days on top of the time you have left.` })}>
                    Pay {naira(plan.price)}
                  </button>
                </>
              ) : (
                <Link to="/billing" className="wal-btn dark sm">Choose a plan</Link>
              )}
            </div>
          </div>

          {data.rentals.map(r => (
            <div className="wal-row" key={r.rnId}>
              <span className="wal-row-ic is-rent"><i className="fa-solid fa-sim-card" /></span>
              <div className="wal-row-main">
                <b>{r.phone}{r.displayName ? ` · ${r.displayName}` : ''}</b>
                <span>
                  Rent Space · {naira(r.price)} / month · {r.status === 'pending_payment' ? 'awaiting first payment' : r.nextBillingAt ? `due ${fmtDate(r.nextBillingAt)} (${dueIn(r.nextBillingAt)})` : '—'}
                </span>
                {r.status === 'paused' && <span className="wal-row-note">Paused by CommandKit — contact support.</span>}
              </div>
              <div className="wal-row-actions">
                <label className="wal-auto">
                  <span>Auto-renew</span>
                  <Switch on={r.autoRenew} disabled={busy === `auto:rental:${r.rnId}` || r.status !== 'active'} onChange={v => toggleAuto('rental', v, r.rnId)} label={`Auto-renew ${r.phone}`} />
                </label>
                <button type="button" className="wal-btn dark sm" disabled={!!busy || r.status === 'paused' || !r.registered}
                  onClick={() => setConfirm({ kind: 'rent', rnId: r.rnId, title: `Pay rent for ${r.phone}`, price: r.price, nonce: nonce(),
                    detail: r.nextBillingAt && parseDate(r.nextBillingAt) > new Date() ? 'Adds one month on top of the time you have left.' : 'Runs one month from now.' })}>
                  Pay {naira(r.price)}
                </button>
              </div>
            </div>
          ))}
          {data.rentals.length === 0 && (
            <div className="wal-row is-empty"><span className="wal-row-ic is-rent"><i className="fa-solid fa-sim-card" /></span><div className="wal-row-main"><b>No rented numbers</b><span>Rent Space rentals you add will show here too.</span></div><Link to="/rent-space" className="wal-link">Rent Space <i className="fa-solid fa-arrow-right" /></Link></div>
          )}
        </div>
      </section>

      {/* History */}
      <section className="wal-card">
        <header className="wal-card-head">
          <div><h2>History</h2><p>{data.ledgerTotal ? `${data.ledgerTotal} transaction${data.ledgerTotal === 1 ? '' : 's'}` : 'Every naira in and out of your wallet'}</p></div>
        </header>
        {ledger.length === 0 ? (
          <div className="wal-empty"><i className="fa-solid fa-receipt" /><b>No transactions yet</b><span>Top up to get started.</span></div>
        ) : (
          <>
            <ul className="wal-ledger">
              {ledger.map(l => {
                const k = KIND[l.lKind] ?? { icon: 'fa-circle', label: l.lKind };
                const credit = l.lType === 'credit';
                return (
                  <li key={l.lId}>
                    <span className={`wal-l-ic ${credit ? 'is-in' : 'is-out'}`}><i className={`fa-solid ${k.icon}`} /></span>
                    <div className="wal-l-main">
                      <b>{l.lNote || k.label}</b>
                      <span>{fmtDate(l.lCreatedAt, true)}{l.lKind === 'admin' ? ` · ${k.label}` : ''}{l.lMeta?.until ? ` · until ${fmtDate(l.lMeta.until)}` : ''}</span>
                    </div>
                    <div className="wal-l-amt">
                      <b className={credit ? 'is-in' : 'is-out'}>{credit ? '+' : '−'}{naira(l.lAmount, 2)}</b>
                      <span>Bal {naira(l.lBalanceAfter, 2)}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
            {ledger.length < (data.ledgerTotal || 0) && (
              <button type="button" className="wal-more" onClick={loadMore} disabled={moreBusy}>{moreBusy ? <span className="wal-spin dark" /> : 'Show more'}</button>
            )}
          </>
        )}
      </section>

      {/* Confirm payment */}
      {confirm && (
        <div className="wal-overlay" onMouseDown={e => { if (e.target === e.currentTarget && !busy) setConfirm(null); }}>
          <div className="wal-modal" role="dialog" aria-label={confirm.title}>
            <span className="wal-grip" aria-hidden="true" />
            <h3>{confirm.title}</h3>
            <p>{confirm.detail}</p>
            <div className="wal-sum">
              <div><span>Price</span><b>{naira(confirm.price, 2)}</b></div>
              <div><span>Wallet balance</span><b>{naira(balance, 2)}</b></div>
              <div className={balance < confirm.price ? 'is-bad' : ''}><span>After payment</span><b>{balance < confirm.price ? 'Not enough' : naira(balance - confirm.price, 2)}</b></div>
            </div>
            <div className="wal-modal-foot">
              <button type="button" className="wal-btn ghost" onClick={() => setConfirm(null)} disabled={!!busy}>Cancel</button>
              {balance < confirm.price ? (
                <button type="button" className="wal-btn brand" onClick={() => { setAmount(String(Math.max(min, Math.ceil(confirm.price - balance)))); setConfirm(null); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                  Top up {naira(Math.max(min, Math.ceil(confirm.price - balance)))}
                </button>
              ) : (
                <button type="button" className="wal-btn brand" onClick={doPay} disabled={!!busy}>
                  {busy ? <><span className="wal-spin" /> Paying…</> : <>Pay {naira(confirm.price)}</>}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}